import { useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { toast, Toaster } from "react-hot-toast";

import { methods } from "../../../utils/axios";
import { userAtom } from "../../../services/atoms/auth.atoms";
import { useAuthActions } from "../../../services/actions/auth.actions";
import { ButtonLoading } from "../../../components/buttons/button-loading";
import InputPass from "../../../components/inputs/input-pass";
import SliderLogin from "../../../components/sliders/slider-login";
import logo from "../../../assets/gm-logo-name.svg";
import { PASSWORD_RULES } from "../../../helpers/password-rules";

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

// Pantalla bloqueante: mientras el usuario tenga el cambio de contraseña
// pendiente, AllRoutes muestra solo esto (el back además rechaza el resto).
const ForcePasswordChange = () => {
  const user = useRecoilValue(userAtom);
  const setUser = useSetRecoilState(userAtom);
  const { signOut } = useAuthActions();
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ code: "", newPassword: "", confirmPassword: "" });

  const email = user?.data?.email || "tu correo";
  const rulesOk = PASSWORD_RULES.every((rule) => rule.test(form.newPassword));
  const matches = form.newPassword !== "" && form.newPassword === form.confirmPassword;
  const canSubmit = /^\d{6}$/.test(form.code) && rulesOk && matches && !saving;

  const onChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const requestCode = async () => {
    setSending(true);
    try {
      await methods.post("/password-change/request-code");
      setCodeSent(true);
      toast.success(`Te enviamos un código a ${email}`, { duration: 5000 });
    } catch (error) {
      toast.error(errorMessage(error, "No pudimos enviar el código. Inténtalo de nuevo."));
    } finally {
      setSending(false);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await methods.post("/password-change/confirm", {
        code: form.code,
        newPassword: form.newPassword,
      });
      localStorage.setItem("token", data.token);
      // "Recuérdame" guardaba la contraseña anterior en una cookie: ya no sirve.
      document.cookie = "_sessionStorage=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/";
      toast.success("Contraseña actualizada", { duration: 4000 });
      setUser((prev) => ({ ...prev, data: { ...prev.data, mustChangePassword: false } }));
    } catch (error) {
      toast.error(errorMessage(error, "No pudimos cambiar la contraseña. Inténtalo de nuevo."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="gm-container-login">
      <div className="gm-form-container">
        <div className="gm-form-section gm-force-password-section">
          <Toaster />
          <div className="gm-login-welcome-container">
            <img src={logo} alt="growme-logo" />
            <h1>Cambia tu contraseña</h1>
            <span>
              Por seguridad, debes crear una nueva contraseña para seguir usando GrowMe.
              Te enviaremos un código de verificación a <strong>{email}</strong>.
            </span>
          </div>

          {!codeSent ? (
            <div className="gm-login-buttons">
              <ButtonLoading
                className="gm-button-login-section"
                type="button"
                buttonLabel="Enviar código"
                onClick={requestCode}
                disabled={sending}
                loading={sending}
              />
            </div>
          ) : (
            <form className="gm-login-form-container" onSubmit={confirm}>
              <div className="gm-login-form-group">
                <label htmlFor="code">
                  <input
                    className="gm-input-form-section"
                    id="code"
                    name="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="Código de 6 dígitos"
                    value={form.code}
                    onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value.replace(/\D/g, "") }))}
                    disabled={saving}
                    required
                  />
                </label>
              </div>
              <InputPass
                className="gm-input-form-section"
                placeholder="Nueva contraseña"
                id="newPassword"
                name="newPassword"
                autoComplete="new-password"
                value={form.newPassword}
                onChange={onChange}
                disabled={saving}
                required
              />
              <InputPass
                className="gm-input-form-section"
                placeholder="Repite la nueva contraseña"
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="new-password"
                value={form.confirmPassword}
                onChange={onChange}
                disabled={saving}
                required
              />
              <ul className="gm-force-password-rules">
                {PASSWORD_RULES.map((rule) => (
                  <li key={rule.label} className={rule.test(form.newPassword) ? "ok" : ""}>
                    {rule.label}
                  </li>
                ))}
                <li className={matches ? "ok" : ""}>Ambas contraseñas coinciden</li>
              </ul>
              <div className="gm-login-buttons">
                <ButtonLoading
                  className="gm-button-login-section"
                  type="submit"
                  buttonLabel="Cambiar contraseña"
                  disabled={!canSubmit}
                  loading={saving}
                />
              </div>
              <button type="button" className="gm-force-password-link" onClick={requestCode} disabled={sending}>
                ¿No te llegó? Enviar otro código
              </button>
            </form>
          )}

          <button type="button" className="gm-force-password-link" onClick={signOut}>
            Cerrar sesión
          </button>
        </div>
      </div>
      <div className="gm-slider-container">
        <SliderLogin />
      </div>
    </div>
  );
};

export default ForcePasswordChange;
